/**
 * The limits on a physician's requests for one request period (CLAUDE.md §5,
 * "Request limits"), as decided by the owner on 26 September 2026:
 *
 * - at most 4 vacation weeks, of which at most 1 is a long-weekend week;
 * - at most 4 preferred weeks;
 * - at most 4 avoid weeks;
 * - vacation and avoid together at most 6;
 * - only one of the Christmas week and the New Year week may be requested
 *   as vacation, and that week counts toward neither the 4 nor the 6
 *   (owner, 27 September 2026).
 *
 * Two preferred weeks in a row are allowed but warned about: the consecutive
 * rule is stronger, so only one of them will be assigned.
 *
 * The admin may exceed all of these when entering availability directly;
 * the limits bind only what physicians submit. Pure, so the request screen
 * and the server run the same check.
 */
import { addDays, type ISODate } from '../dates/iso.ts';
import type { Week } from '../weeks/weeks.ts';

export type RequestKind = 'vacation' | 'preferred' | 'avoid';

export interface WeekRequest {
  weekStart: ISODate;
  kind: RequestKind;
}

export const REQUEST_LIMITS = {
  vacation: 4,
  vacationLongWeekends: 1,
  preferred: 4,
  avoid: 4,
  vacationPlusAvoid: 6,
  /** One of the Christmas week and the New Year week. */
  vacationSpecial: 1,
} as const;

export interface RequestCounts {
  /** Vacation weeks other than the Christmas or New Year week. */
  vacation: number;
  /** Vacation on the Christmas or the New Year week: 0 or 1 when within the rule. */
  vacationSpecial: number;
  vacationLongWeekends: number;
  preferred: number;
  avoid: number;
  vacationPlusAvoid: number;
}

export type RequestProblemKind =
  | 'too-many-vacation'
  | 'too-many-vacation-long-weekends'
  | 'too-many-preferred'
  | 'too-many-avoid'
  | 'too-many-vacation-plus-avoid'
  | 'both-special-weeks-vacation'
  | 'unknown-week'
  | 'duplicate-week';

export interface RequestProblem {
  kind: RequestProblemKind;
  /** The weeks involved, so the screen can point at them. */
  weekStarts: ISODate[];
}

export interface RequestWarning {
  kind: 'consecutive-preferred';
  weekStarts: ISODate[];
}

export interface RequestCheck {
  counts: RequestCounts;
  problems: RequestProblem[];
  warnings: RequestWarning[];
  ok: boolean;
}

/**
 * Whether a period has a Christmas or a New Year week, so the request screen
 * explains the Christmas/New Year vacation rule (and counts it) only where
 * it can apply. A period from January to July has neither, and telling a
 * physician about a week they cannot pick only raises a question. This
 * decides what is shown, never what is allowed: checkRequests applies the
 * same rules whatever the period holds.
 */
export function hasSpecialWeeks(weeks: readonly Pick<Week, 'kind'>[]): boolean {
  return weeks.some((w) => w.kind === 'christmas' || w.kind === 'new-year');
}

export function checkRequests(weeks: readonly Week[], requests: readonly WeekRequest[]): RequestCheck {
  const byStart = new Map(weeks.map((w) => [w.weekStart, w]));
  const problems: RequestProblem[] = [];
  const warnings: RequestWarning[] = [];

  const seen = new Set<ISODate>();
  const duplicates: ISODate[] = [];
  const unknown: ISODate[] = [];
  for (const r of requests) {
    if (!byStart.has(r.weekStart)) unknown.push(r.weekStart);
    if (seen.has(r.weekStart)) duplicates.push(r.weekStart);
    seen.add(r.weekStart);
  }
  if (unknown.length > 0) problems.push({ kind: 'unknown-week', weekStarts: unknown });
  if (duplicates.length > 0) problems.push({ kind: 'duplicate-week', weekStarts: duplicates });

  const of = (kind: RequestKind) => requests.filter((r) => r.kind === kind && byStart.has(r.weekStart));
  const allVacation = of('vacation');
  const preferred = of('preferred');
  const avoid = of('avoid');
  const isSpecialWeek = (r: WeekRequest) => {
    const k = byStart.get(r.weekStart)?.kind;
    return k === 'christmas' || k === 'new-year';
  };
  // The Christmas or New Year week is on top of the ordinary allowance.
  const specialVacation = allVacation.filter(isSpecialWeek);
  const vacation = allVacation.filter((r) => !isSpecialWeek(r));
  const vacationLongWeekends = vacation.filter((r) => byStart.get(r.weekStart)?.kind === 'long-weekend');
  const counts: RequestCounts = {
    vacation: vacation.length,
    vacationSpecial: specialVacation.length,
    vacationLongWeekends: vacationLongWeekends.length,
    preferred: preferred.length,
    avoid: avoid.length,
    vacationPlusAvoid: vacation.length + avoid.length,
  };

  const starts = (list: WeekRequest[]) => list.map((r) => r.weekStart);
  if (counts.vacation > REQUEST_LIMITS.vacation) problems.push({ kind: 'too-many-vacation', weekStarts: starts(vacation) });
  if (counts.vacationLongWeekends > REQUEST_LIMITS.vacationLongWeekends) {
    problems.push({ kind: 'too-many-vacation-long-weekends', weekStarts: starts(vacationLongWeekends) });
  }
  if (counts.preferred > REQUEST_LIMITS.preferred) problems.push({ kind: 'too-many-preferred', weekStarts: starts(preferred) });
  if (counts.avoid > REQUEST_LIMITS.avoid) problems.push({ kind: 'too-many-avoid', weekStarts: starts(avoid) });
  if (counts.vacationPlusAvoid > REQUEST_LIMITS.vacationPlusAvoid) {
    problems.push({ kind: 'too-many-vacation-plus-avoid', weekStarts: [...starts(vacation), ...starts(avoid)] });
  }

  // Christmas and New Year: one of the two at most, per pair of special weeks.
  if (specialVacation.length > 1) {
    // Two special weeks in one period are almost always the pair; over a
    // longer period there could be two pairs, so group by adjacency.
    const sorted = starts(specialVacation).sort();
    const offending: ISODate[] = [];
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const cur = sorted[i];
      if (prev !== undefined && cur !== undefined && addDays(prev, 7) === cur) offending.push(prev, cur);
    }
    if (offending.length > 0) problems.push({ kind: 'both-special-weeks-vacation', weekStarts: [...new Set(offending)] });
  }

  // Preferred weeks in a row: only one of each run will be assigned.
  const preferredStarts = new Set(starts(preferred));
  const runs: ISODate[] = [];
  for (const s of preferredStarts) if (preferredStarts.has(addDays(s, 7))) runs.push(s, addDays(s, 7));
  if (runs.length > 0) warnings.push({ kind: 'consecutive-preferred', weekStarts: [...new Set(runs)].sort() });

  return { counts, problems, warnings, ok: problems.length === 0 };
}

/** The few figures of a check that fit the one-line request bar on a phone. */
export interface RequestSummary {
  /** Every vacation week chosen, the Christmas or New Year week included. */
  vacation: number;
  preferred: number;
  avoid: number;
  /** Warnings: allowed, but worth reading (two prefer-call weeks in a row). */
  notes: number;
  /** A limit is broken, so the requests cannot be submitted. */
  overLimit: boolean;
}

/**
 * The phone's compact line says what was chosen, not how it counts against
 * each limit: so its vacation figure includes the Christmas or New Year week,
 * which the full counters keep apart because it is on top of the 4. The
 * full counters stay one press away.
 */
export function summarizeRequests(check: RequestCheck): RequestSummary {
  return {
    vacation: check.counts.vacation + check.counts.vacationSpecial,
    preferred: check.counts.preferred,
    avoid: check.counts.avoid,
    notes: check.warnings.length,
    overLimit: !check.ok,
  };
}
